import React, { useState, useMemo } from 'react'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import type { SharedProps } from '@/types/shared'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'

const DAYS = [
  { key: 'mon', label: 'Mon', full: 'Monday' },
  { key: 'tue', label: 'Tue', full: 'Tuesday' },
  { key: 'wed', label: 'Wed', full: 'Wednesday' },
  { key: 'thu', label: 'Thu', full: 'Thursday' },
  { key: 'fri', label: 'Fri', full: 'Friday' },
  { key: 'sat', label: 'Sat', full: 'Saturday' },
  { key: 'sun', label: 'Sun', full: 'Sunday' },
] as const

type ModifierType = 'percentage' | 'absolute'

interface ModifierHash {
  type: string
  value: number
}

interface DayOfWeekProps extends SharedProps {
  modifiers: Record<string, number | ModifierHash>
}

function parseInitialModifiers(raw: Record<string, number | ModifierHash>): {
  type: ModifierType
  values: Record<string, string>
} {
  let detectedType: ModifierType = 'percentage'
  const values: Record<string, string> = {}

  for (const day of DAYS) {
    const entry = raw[day.key]
    if (entry != null && typeof entry === 'object' && 'type' in entry) {
      values[day.key] = String(entry.value ?? 0)
      if (entry.type === 'absolute') {
        detectedType = 'absolute'
      }
    } else {
      values[day.key] = entry != null ? String(entry) : '0'
    }
  }

  return { type: detectedType, values }
}

export default function DayOfWeek({
  currentUser,
  property,
  properties,
  modifiers: initialModifiers,
}: DayOfWeekProps) {
  const initial = useMemo(() => parseInitialModifiers(initialModifiers), [initialModifiers])

  const [modifierType, setModifierType] = useState<ModifierType>(initial.type)
  const [modifiers, setModifiers] = useState<Record<string, string>>(initial.values)
  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const sym = currencySymbol(property.currency)
  const base = property.basePath

  const isDirty = useMemo(() => {
    if (modifierType !== initial.type) return true
    for (const day of DAYS) {
      if (modifiers[day.key] !== initial.values[day.key]) return true
    }
    return false
  }, [modifiers, modifierType, initial])

  function handleTypeChange(newType: ModifierType) {
    if (newType === modifierType) return
    setModifierType(newType)
    // Reset all values to 0 when switching modes
    const reset: Record<string, string> = {}
    for (const day of DAYS) {
      reset[day.key] = '0'
    }
    setModifiers(reset)
  }

  function handleChange(dayKey: string, value: string) {
    setModifiers((prev) => ({ ...prev, [dayKey]: value }))
  }

  // The save goes through the server review first (change safety R1.5): the same payload is
  // reviewed, then sent by the same PATCH as before once the operator confirms.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const hashModifiers: Record<string, { type: string; value: number }> = {}
    for (const day of DAYS) {
      const rawValue = Number(modifiers[day.key]) || 0
      hashModifiers[day.key] = { type: modifierType, value: rawValue }
    }

    const payload = { modifiers: hashModifiers }
    review.open(`${base}/pricing-rules/day-of-week/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/day-of-week`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  const descriptionText =
    modifierType === 'percentage'
      ? 'Percentage adjustments applied to base rates for each day of the week. Positive values increase rates, negative values decrease them.'
      : `Fixed amount adjustments (${sym}) added to or subtracted from base rates for each day of the week.`

  const tooltipContent =
    modifierType === 'percentage'
      ? `Apply percentage adjustments for specific days. For example, +10% on Fridays means a ${sym}100 base rate becomes ${sym}110.`
      : `Apply fixed amount adjustments for specific days. For example, +${sym}15 on Fridays means a ${sym}100 base rate becomes ${sym}115.`

  return (
    <OperatorLayout title="Day of Week" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="day-of-week"
          help={{ content: tooltipContent, learnMore: '/guides/day-of-week' }}
        />

        {/* Form */}
        <form onSubmit={handleSubmit} className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          <div className="rounded-xl border border-border/60 bg-white p-6 shadow-sm">
            <p className="mb-5 text-sm text-muted-foreground">
              {descriptionText}
            </p>

            {/* Mode toggle */}
            <div className="mb-5 inline-flex h-9 items-center rounded-md bg-muted p-1">
              <button
                type="button"
                onClick={() => handleTypeChange('percentage')}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-sm text-sm font-medium transition-all',
                  modifierType === 'percentage'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Percentage (%)
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('absolute')}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-sm text-sm font-medium transition-all',
                  modifierType === 'absolute'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Fixed Amount ({sym})
              </button>
            </div>

            <div className="grid grid-cols-7 gap-3">
              {DAYS.map((day) => {
                const val = Number(modifiers[day.key]) || 0
                const isPositive = val > 0
                const isNegative = val < 0
                return (
                  <div
                    key={day.key}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-xl border p-4 transition-all',
                      isPositive && 'border-emerald-200 bg-emerald-50/50',
                      isNegative && 'border-rose-200 bg-rose-50/50',
                      !isPositive && !isNegative && 'border-border/60 bg-white'
                    )}
                  >
                    <label
                      htmlFor={`dow-${day.key}`}
                      className="text-xs font-semibold uppercase tracking-wider cursor-pointer text-muted-foreground"
                    >
                      {day.label}
                    </label>
                    <div className="relative">
                      <Input
                        id={`dow-${day.key}`}
                        type="number"
                        step={modifierType === 'absolute' ? '0.01' : '1'}
                        className={cn(
                          'w-20 text-center rounded-lg border-border/60 font-semibold',
                          modifierType === 'absolute' ? 'pl-6 pr-2' : 'pr-7'
                        )}
                        value={modifiers[day.key]}
                        onChange={(e) => handleChange(day.key, e.target.value)}
                      />
                      {modifierType === 'percentage' ? (
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                          %
                        </span>
                      ) : (
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                          {sym}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">{day.full}</span>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
